import React, { useState } from "react";
import { CardComp, ButtonComp, ModalCompAdvance } from "../../../../components";
import Message from "../../../../components/MessageModal";
import { toast } from "react-hot-toast";
import moment from "moment";
import {
  formatChannelName,
  getChannelIcon,
  getStatusBadgeColor,
  getStatusBadgeText,
  formatRecipientName,
  truncateMessage,
  getChannelColor,
} from "../../../../utils/communicationUtils";
import { convertToTitleCase } from "../../../../utils/helpers";
import { useResendCommunicationsMutation, useGetSingleEventWithHistoryQuery } from "../../../../store/admin/eventSlice";

const CommunicationHistory = ({ communicationHistory = {}, isLoading = false, eventId }) => {
  const [activeChannel, setActiveChannel] = useState("email");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isResending, setIsResending] = useState(false);
  
  const [resendCommunications] = useResendCommunicationsMutation();
  
  // Refetch history after resend
  const { refetch: refetchHistory } = useGetSingleEventWithHistoryQuery(eventId, {
    skip: !eventId,
  });

  // Check if there's any history at all
  const hasHistory = Object.values(communicationHistory || {}).some(
    (channelData) => channelData?.total > 0
  );

  // Channel tabs configuration
  const channelTabs = [
    {
      name: "Email",
      key: "email",
      count: communicationHistory?.email?.total || 0,
    },
    {
      name: "SMS",
      key: "sms",
      count: communicationHistory?.sms?.total || 0,
    },
    {
      name: "Push",
      key: "push",
      count: communicationHistory?.push?.total || 0,
    },
  ];

  // Filter tabs to only show channels with history
  const visibleTabs = hasHistory
    ? channelTabs.filter((tab) => (communicationHistory?.[tab.key]?.total || 0) > 0)
    : [];

  // Get current channel data
  const currentChannelData = communicationHistory?.[activeChannel] || {
    total: 0,
    sent: 0,
    failed: 0,
    delivered: 0,
    pending: 0,
    logs: [],
  };

  // Statistics cards for the active channel
  const StatisticsCard = ({ label, value, color, icon }) => (
    <CardComp cardClassName="border-0 shadow-sm h-100">
      <div className="p-3">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <i className={icon} style={{ color, fontSize: "20px" }}></i>
          <span
            className="px-2 py-1 rounded-pill text-white fw-semibold"
            style={{
              backgroundColor: color,
              fontSize: "11px",
              minWidth: "fit-content",
            }}
          >
            {value}
          </span>
        </div>
        <p className="text-muted mb-0 small" style={{ fontSize: "12px" }}>
          {label}
        </p>
      </div>
    </CardComp>
  );

  // Render a single log entry
  const renderLogEntry = (log, index) => {
    const recipientName = formatRecipientName(log?.recipient);
    const recipientType = convertToTitleCase(log?.recipient_type || "Unknown");
    const statusColor = getStatusBadgeColor(log?.status);
    const statusText = getStatusBadgeText(log?.status);
    const channelIcon = getChannelIcon(log?.channel);
    const channelColor = getChannelColor(log?.channel);

    return (
      <div key={log?.id || index} className="col-12 mb-3">
        <CardComp cardClassName="border shadow-sm">
          <div className="p-3">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div className="flex-1">
                <div className="d-flex align-items-center gap-2 mb-1">
                  <i
                    className={channelIcon}
                    style={{ color: channelColor, fontSize: "16px" }}
                  ></i>
                  <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "15px" }}>
                    {formatChannelName(log?.channel)}
                  </h6>
                </div>
                <p className="text-muted mb-0 small" style={{ fontSize: "12px" }}>
                  To: {recipientName} ({recipientType})
                </p>
                {log?.subject && (
                  <p className="text-dark mb-0 mt-1 fw-semibold" style={{ fontSize: "13px" }}>
                    {log.subject}
                  </p>
                )}
              </div>
              <span
                className="px-2 py-1 rounded-pill text-white fw-semibold"
                style={{
                  backgroundColor: statusColor,
                  fontSize: "11px",
                  minWidth: "fit-content",
                  whiteSpace: "nowrap",
                }}
              >
                {statusText}
              </span>
            </div>

            {/* Message Preview */}
            {log?.message && (
              <div className="mb-2 mt-2">
                <p className="text-muted mb-1 small" style={{ fontSize: "11px" }}>
                  Message:
                </p>
                <p className="text-dark mb-0" style={{ fontSize: "13px" }}>
                  {truncateMessage(log.message, 150)}
                </p>
              </div>
            )}

            {/* Error Message */}
            {log?.error_message && (
              <div className="mb-2 mt-2">
                <p className="text-danger mb-1 small fw-semibold" style={{ fontSize: "11px" }}>
                  Error:
                </p>
                <p className="text-danger mb-0 small" style={{ fontSize: "12px" }}>
                  {log.error_message}
                </p>
              </div>
            )}

            {/* Timestamps */}
            <div className="row g-2 mt-2 pt-2 border-top">
              {log?.sent_at && (
                <div className="col-6">
                  <p className="text-muted mb-0 small" style={{ fontSize: "11px" }}>
                    Sent At
                  </p>
                  <p className="fw-semibold text-dark mb-0" style={{ fontSize: "12px" }}>
                    {moment(log.sent_at).format("DD-MMM-YYYY HH:mm")}
                  </p>
                </div>
              )}
              {log?.delivered_at && (
                <div className="col-6">
                  <p className="text-muted mb-0 small" style={{ fontSize: "11px" }}>
                    Delivered At
                  </p>
                  <p className="fw-semibold text-dark mb-0" style={{ fontSize: "12px" }}>
                    {moment(log.delivered_at).format("DD-MMM-YYYY HH:mm")}
                  </p>
                </div>
              )}
              {!log?.delivered_at && log?.sent_at && (
                <div className="col-6">
                  <p className="text-muted mb-0 small" style={{ fontSize: "11px" }}>
                    Created At
                  </p>
                  <p className="fw-semibold text-dark mb-0" style={{ fontSize: "12px" }}>
                    {moment(log.created_at).format("DD-MMM-YYYY HH:mm")}
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardComp>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="py-3">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading communication history...</p>
        </div>
      </div>
    );
  }

  // Handle resend communications
  const handleResend = async () => {
    if (!eventId) {
      toast.error("Event ID is required");
      return;
    }

    setIsResending(true);
    setShowConfirmModal(false);

    const toastId = toast.loading("Resending communications...");

    try {
      const result = await resendCommunications(eventId).unwrap();
      
      if (result?.status) {
        toast.success(result?.message || "Communications are being resent. This may take a few moments.", {
          id: toastId,
        });
        
        // Refetch history after a short delay to see new logs
        setTimeout(() => {
          refetchHistory();
        }, 2000);
      } else {
        toast.error(result?.message || "Failed to resend communications", {
          id: toastId,
        });
      }
    } catch (error) {
      console.error("Resend communications error:", error);
      toast.error(error?.data?.message || "Failed to resend communications. Please try again.", {
        id: toastId,
      });
    } finally {
      setIsResending(false);
    }
  };

  const confirmationModal = [
    {
      name: "Resend Confirmation",
      component: (
        <Message
          title="Resend Communications"
          desc="Are you sure you want to resend all communications for this event? This will send emails, SMS, and push notifications to all recipients again. SMS units will be deducted for each SMS sent."
          onBack={() => setShowConfirmModal(false)}
          onProceed={handleResend}
          loading={isResending}
        />
      ),
    },
  ];

  if (!hasHistory) {
    return (
      <div className="py-4">
        <div className="text-center">
          <i
            className="fas fa-inbox"
            style={{ fontSize: "48px", color: "#A1A0A0", marginBottom: "16px" }}
          ></i>
          <p className="text-muted mb-0">No communication history available</p>
          <p className="text-muted small mt-2">
            Communication logs will appear here once notifications are sent for this event.
          </p>
          {eventId && (
            <div className="mt-4">
              <ButtonComp
                btnText="Resend Communications"
                btnClassName="bg-primary1 text-white border-0 px-4 py-2 rounded"
                onClick={() => setShowConfirmModal(true)}
                isLoading={isResending}
              />
            </div>
          )}
        </div>
        
        {/* Confirmation Modal */}
        <ModalCompAdvance
          modalBody={0}
          show={showConfirmModal}
          arrayComp={confirmationModal}
          handleClose={() => setShowConfirmModal(false)}
          centered
          size="md"
        />
      </div>
    );
  }

  return (
    <div className="py-3">
      {/* Resend Button */}
      {eventId && (
        <div className="d-flex justify-content-end mb-4">
          <ButtonComp
            btnText="Resend Communications"
            btnClassName="bg-primary1 text-white border-0 px-4 py-2 rounded"
            onClick={() => setShowConfirmModal(true)}
            isLoading={isResending}
            disabled={isResending}
          />
        </div>
      )}

      {/* Statistics Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-3 col-6">
          <StatisticsCard
            label="Total Sent"
            value={currentChannelData.total}
            color={getChannelColor(activeChannel)}
            icon={getChannelIcon(activeChannel)}
          />
        </div>
        <div className="col-md-3 col-6">
          <StatisticsCard
            label="Successfully Sent"
            value={currentChannelData.sent + currentChannelData.delivered}
            color="#19BC31"
            icon="fas fa-check-circle"
          />
        </div>
        <div className="col-md-3 col-6">
          <StatisticsCard
            label="Delivered"
            value={currentChannelData.delivered}
            color="var(--color-secondary)"
            icon="fas fa-check-double"
          />
        </div>
        <div className="col-md-3 col-6">
          <StatisticsCard
            label="Failed"
            value={currentChannelData.failed}
            color={currentChannelData.failed > 0 ? "#FF4343" : "#A1A0A0"}
            icon="fas fa-exclamation-circle"
          />
        </div>
      </div>

      {/* Channel Tabs */}
      {visibleTabs.length > 1 && (
        <div className="mb-4">
          <div className="d-flex gap-4 border-bottom">
            {visibleTabs.map((tab) => (
              <div
                key={tab.key}
                className={`pb-2 pointer fw-bold transition-all ${
                  activeChannel === tab.key
                    ? "text-primary1 border-bottom border-primary1 border-3"
                    : "text-blacklight hover:text-black1"
                }`}
                onClick={() => setActiveChannel(tab.key)}
                style={{ fontSize: "16px", cursor: "pointer" }}
              >
                {tab.name} ({tab.count})
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Logs List */}
      <div className="mt-3">
        {currentChannelData.logs && currentChannelData.logs.length > 0 ? (
          <div className="row">
            {currentChannelData.logs.map((log, index) => renderLogEntry(log, index))}
          </div>
        ) : (
          <div className="text-center py-4">
            <i
              className="fas fa-inbox"
              style={{ fontSize: "36px", color: "#A1A0A0", marginBottom: "12px" }}
            ></i>
            <p className="text-muted mb-0">
              No {formatChannelName(activeChannel)} communications found
            </p>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <ModalCompAdvance
        modalBody={0}
        show={showConfirmModal}
        arrayComp={confirmationModal}
        handleClose={() => setShowConfirmModal(false)}
        centered
        size="md"
      />
    </div>
  );
};

export default CommunicationHistory;
